import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'
import type { EventLog } from '../lib/types'

/** Friendly label + category color for every event_type the backend logs
 * (see services/event_logger.py call sites). Falls back to a readable
 * title-cased version of the raw string for anything not listed here, so a
 * new event type added later never renders as a dead blank. */
const EVENT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  organization_created: { label: 'Organization created', color: 'badge-blue' },
  organization_updated: { label: 'Organization updated', color: 'badge-blue' },
  organization_signed_up: { label: 'Organization signed up', color: 'badge-blue' },
  practice_created: { label: 'Practice created', color: 'badge-blue' },
  provider_created: { label: 'Provider created', color: 'badge-blue' },
  provider_updated: { label: 'Provider updated', color: 'badge-blue' },
  provider_invited: { label: 'Provider invited', color: 'badge-blue' },
  provider_invite_accepted: { label: 'Provider invite accepted', color: 'badge-green' },
  provider_invite_resent: { label: 'Provider invite resent', color: 'badge-blue' },
  provider_onboarding_completed: { label: 'Provider onboarding completed', color: 'badge-green' },
  provider_seat_activated: { label: 'Provider seat activated', color: 'badge-green' },
  org_seat_limit_reached: { label: 'Organization reached seat limit', color: 'badge-amber' },
  credentialing_submitted: { label: 'Credentialing submitted', color: 'badge-blue' },
  credentialing_in_review: { label: 'Credentialing in review', color: 'badge-amber' },
  credentialing_approved: { label: 'Credentialing approved', color: 'badge-green' },
  credentialing_rejected: { label: 'Credentialing rejected', color: 'badge-red' },
  credentialing_note_added: { label: 'Credentialing note added', color: 'badge-grey' },
  patient_created: { label: 'Patient created', color: 'badge-blue' },
  patient_signed_up: { label: 'Patient signed up', color: 'badge-blue' },
  patient_self_assigned_to_organization: { label: 'Patient assigned to organization', color: 'badge-blue' },
  intake_started: { label: 'Intake started', color: 'badge-blue' },
  intake_completed: { label: 'Intake completed', color: 'badge-green' },
  intake_updated: { label: 'Intake updated', color: 'badge-blue' },
  match_requested: { label: 'Match requested', color: 'badge-blue' },
  match_generated: { label: 'Match generated', color: 'badge-blue' },
  case_created: { label: 'Case created', color: 'badge-blue' },
  case_closed: { label: 'Case closed', color: 'badge-grey' },
  case_reached_session_4: { label: 'Reached Session 4', color: 'badge-green' },
  case_dropped_before_session_4: { label: 'Dropped before Session 4', color: 'badge-red' },
  session_attended: { label: 'Session attended', color: 'badge-green' },
  session_cancelled: { label: 'Session cancelled', color: 'badge-grey' },
  session_no_show: { label: 'Session no-show', color: 'badge-amber' },
  session_rescheduled: { label: 'Session rescheduled', color: 'badge-grey' },
  alert_created: { label: 'Continuity alert created', color: 'badge-amber' },
  alert_viewed: { label: 'Continuity alert viewed', color: 'badge-grey' },
  alert_resolved: { label: 'Continuity alert resolved', color: 'badge-green' },
  risk_score_generated: { label: 'Risk score generated', color: 'badge-grey' },
  intervention_created: { label: 'Intervention created', color: 'badge-blue' },
  intervention_completed: { label: 'Intervention completed', color: 'badge-green' },
  admin_note_added: { label: 'Note added', color: 'badge-grey' },
  admin_task_assigned: { label: 'Task assigned', color: 'badge-blue' },
  admin_task_completed: { label: 'Task completed', color: 'badge-green' },
  user_logged_in: { label: 'Logged in', color: 'badge-grey' },
  user_logged_out: { label: 'Logged out', color: 'badge-grey' },
  admin_logged_in: { label: 'Admin logged in', color: 'badge-blue' },
  admin_logged_out: { label: 'Admin logged out', color: 'badge-grey' },
}

function friendlyLabel(eventType: string): { label: string; color: string } {
  if (EVENT_TYPE_LABELS[eventType]) return EVENT_TYPE_LABELS[eventType]
  const label = eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return { label, color: 'badge-grey' }
}

type ScopeProps =
  | { organizationId: string; providerId?: never; patientId?: never }
  | { providerId: string; organizationId?: never; patientId?: never }
  | { patientId: string; organizationId?: never; providerId?: never }

export function Timeline({
  limit = 20, filterTypes, emptyLabel, ...scope
}: ScopeProps & { limit?: number; filterTypes?: string[]; emptyLabel?: string }) {
  const [events, setEvents] = useState<EventLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ limit: String(limit) })
    if (scope.organizationId) params.set('organization_id', scope.organizationId)
    if (scope.providerId) params.set('provider_id', scope.providerId)
    if (scope.patientId) params.set('patient_id', scope.patientId)
    api(`/admin/event-logs?${params.toString()}`).then((rows) => { setEvents(rows); setLoading(false) })
  }, [scope.organizationId, scope.providerId, scope.patientId, limit])

  if (loading) return <div className="muted" style={{ fontSize: 13 }}>Loading timeline…</div>

  const visible = filterTypes ? events.filter((e) => filterTypes.includes(e.event_type)) : events

  return (
    <div className="timeline">
      {visible.map((e) => {
        const { label, color } = friendlyLabel(e.event_type)
        return (
          <div key={e.id} className="timeline-item">
            <div className="ti-type">
              <span className={`badge ${color}`}>{label}</span>
              {e.entity_type && e.entity_type !== 'organization' && (
                <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                  {e.entity_type}{e.entity_id ? ` · ${e.entity_id.slice(0, 8)}` : ''}
                </span>
              )}
            </div>
            <div className="ti-time">{new Date(e.created_at).toLocaleString()}</div>
          </div>
        )
      })}
      {visible.length === 0 && <div className="empty">{emptyLabel || 'No activity recorded yet.'}</div>}
    </div>
  )
}
